/** The one mechanism behind "a scrollbar shows only while the user is active" (owner, 23 Oct 2026).
 *
 *  CSS can see a hovered or focused element but not an idle pointer, so this module keeps the state the
 *  stylesheet needs: document-level capture listeners mark the scroll container the user is working
 *  with as `data-scroll-activity="active"` and set it to `"idle"` once nothing has touched it for
 *  `SCROLL_IDLE_MS`. The thumb paint and its fade live in `app/styles/base.css`, keyed only on that
 *  attribute, so every scroller (host pages, plugin pages, the shared ScrollArea) follows the same rule
 *  without a hook of its own.
 *
 *  A container is active while
 *  - the user scrolled it: a scroll event within `SCROLL_IDLE_MS` of a wheel, a pointer press, a touch
 *    move or a scrolling key. Programmatic scrolls on their own, such as the transcript following a
 *    streaming answer, do not count, or the bar would show for the whole reply of a reader who never
 *    moved;
 *  - the pointer moved over it (the nearest scroll container under the pointer);
 *  - the pointer rests on, or drags, its own scrollbar strip. That keeps a grabbed or hovered thumb
 *    visible however long the pointer stays still; a hovered strip is released when the pointer leaves
 *    the document. */

import { uiZoom } from './uiZoom';

export const SCROLL_ACTIVITY_ATTRIBUTE = 'data-scroll-activity';
export const SCROLL_IDLE_MS = 1200;

/** Keys whose default action scrolls; typed into a field they edit text instead. */
const SCROLL_KEYS = new Set(['PageUp', 'PageDown', 'Home', 'End', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' ']);

function scrolls(overflow: string): boolean {
  return overflow === 'auto' || overflow === 'scroll';
}

function isEditable(target: EventTarget | null): boolean {
  return target instanceof HTMLElement
    && (target.isContentEditable || target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement);
}

/** Installs the listeners on `doc` and returns their removal. Mounted once, by the root providers. */
export function installScrollActivity(doc: Document): () => void {
  const lastSeen = new Map<Element, number>();
  const timers = new Map<Element, ReturnType<typeof setTimeout>>();
  let lastInput = Number.NEGATIVE_INFINITY;
  /** The scroller whose own scrollbar strip is under the pointer or being dragged; it does not go idle. */
  let held: Element | null = null;
  let pendingPointer: PointerEvent | null = null;
  let frame = 0;

  function settle(element: Element): void {
    timers.delete(element);
    // Released by `hold`, which marks it again and so restarts its countdown.
    if (element === held) return;
    const left = lastSeen.get(element)! + SCROLL_IDLE_MS - Date.now();
    if (left > 0) {
      timers.set(element, setTimeout(settle, left, element));
      return;
    }
    lastSeen.delete(element);
    element.setAttribute(SCROLL_ACTIVITY_ATTRIBUTE, 'idle');
  }

  /** One timer per container, re-armed for the remainder when it fires, so a 60 Hz scroll costs a map
   *  write per event rather than a clearTimeout/setTimeout pair. */
  function mark(element: Element): void {
    lastSeen.set(element, Date.now());
    if (element.getAttribute(SCROLL_ACTIVITY_ATTRIBUTE) !== 'active') element.setAttribute(SCROLL_ACTIVITY_ATTRIBUTE, 'active');
    if (!timers.has(element)) timers.set(element, setTimeout(settle, SCROLL_IDLE_MS, element));
  }

  function hold(element: Element | null): void {
    if (element === held) return;
    const released = held;
    held = element;
    if (released) mark(released);
  }

  function nearestScroller(start: Element): Element | null {
    for (let node: Element | null = start; node; node = node.parentElement) {
      const tall = node.scrollHeight > node.clientHeight;
      const wide = node.scrollWidth > node.clientWidth;
      if (!tall && !wide) continue;
      const style = getComputedStyle(node);
      if ((tall && scrolls(style.overflowY)) || (wide && scrolls(style.overflowX))) return node;
    }
    return null;
  }

  /** Whether the point lies in the element's own scrollbar strip: inside its border box, past its client
   *  area. The app is left-to-right, so a vertical bar is on the right. */
  function onOwnScrollbar(element: Element, x: number, y: number): boolean {
    const box = element.getBoundingClientRect();
    if (x < box.left || x >= box.right || y < box.top || y >= box.bottom) return false;
    // Client dimensions are layout pixels; the rect and pointer already include the root UI zoom.
    const zoom = uiZoom();
    return x >= box.left + (element.clientLeft + element.clientWidth) * zoom || y >= box.top + (element.clientTop + element.clientHeight) * zoom;
  }

  /** Pointer work is coalesced to one walk per frame; only the latest position matters. */
  function flushPointer(): void {
    frame = 0;
    const event = pendingPointer!;
    pendingPointer = null;
    const scroller = event.target instanceof Element ? nearestScroller(event.target) : null;
    // A drag that started on a scrollbar keeps it held wherever the pointer goes until the button is up.
    if (held && event.buttons !== 0) {
      if (scroller) mark(scroller);
      return;
    }
    if (!scroller) {
      hold(null);
      return;
    }
    mark(scroller);
    hold(onOwnScrollbar(scroller, event.clientX, event.clientY) ? scroller : null);
  }

  const onPointer = (event: PointerEvent) => {
    if (event.pointerType === 'touch') return;
    pendingPointer = event;
    if (!frame) frame = requestAnimationFrame(flushPointer);
  };
  /** The pointer left the document (`relatedTarget` is null) without a button down: nothing else will
   *  report it, so the hovered strip is released here and a move still queued for the frame is dropped,
   *  or it would hold the bar again. A thumb dragged out of the window stays held until the drag ends. */
  const onPointerOut = (event: PointerEvent) => {
    if (event.relatedTarget !== null || event.buttons !== 0) return;
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    pendingPointer = null;
    hold(null);
  };
  const onInput = () => {
    lastInput = Date.now();
  };
  const onKey = (event: KeyboardEvent) => {
    if (SCROLL_KEYS.has(event.key) && !isEditable(event.target)) lastInput = Date.now();
  };
  const onScroll = (event: Event) => {
    if (event.target instanceof Element && Date.now() - lastInput <= SCROLL_IDLE_MS) mark(event.target);
  };

  const options = { capture: true, passive: true } as const;
  doc.addEventListener('pointermove', onPointer, options);
  doc.addEventListener('pointerup', onPointer, options);
  doc.addEventListener('pointerout', onPointerOut, options);
  doc.addEventListener('pointerdown', onInput, options);
  doc.addEventListener('wheel', onInput, options);
  doc.addEventListener('touchmove', onInput, options);
  doc.addEventListener('keydown', onKey, options);
  doc.addEventListener('scroll', onScroll, options);

  return () => {
    doc.removeEventListener('pointermove', onPointer, options);
    doc.removeEventListener('pointerup', onPointer, options);
    doc.removeEventListener('pointerout', onPointerOut, options);
    doc.removeEventListener('pointerdown', onInput, options);
    doc.removeEventListener('wheel', onInput, options);
    doc.removeEventListener('touchmove', onInput, options);
    doc.removeEventListener('keydown', onKey, options);
    doc.removeEventListener('scroll', onScroll, options);
    if (frame) cancelAnimationFrame(frame);
    for (const timer of timers.values()) clearTimeout(timer);
    timers.clear();
    lastSeen.clear();
    held = null;
  };
}
